<template>
    <div v-if="showHost">
        <div
            v-if="showOffer || showActivatedDiscount || showActiveGift"
            class="sms-project-promo"
            role="region"
            :aria-label="showActivatedDiscount || isDiscount ? 'Скидка на CRM' : 'Подарок SMS'"
        >
            <span class="sms-project-promo__icon" aria-hidden="true">
                <img :src="smsLogo" alt="" />
            </span>
            <div class="sms-project-promo__copy">
                <div class="sms-project-promo__title">{{ bannerTitle }}</div>
                <div class="sms-project-promo__sub">{{ bannerSub }}</div>
            </div>
            <div v-if="showConnect || showOffer || showRenew" class="sms-project-promo__actions">
                <button
                    v-if="showConnect"
                    type="button"
                    class="sms-project-promo__btn"
                    :disabled="connecting"
                    :aria-busy="connecting ? 'true' : 'false'"
                    @click="connectExistingGift"
                >
                    <v-progress-circular
                        v-if="connecting"
                        indeterminate
                        size="16"
                        width="2"
                        color="#fff"
                    />
                    <span v-else>Подключить</span>
                </button>
                <button
                    v-if="showOffer || showRenew"
                    type="button"
                    class="sms-project-promo__btn"
                    :class="{ 'sms-project-promo__btn--quiet': showConnect }"
                    :disabled="isActivating || renewing"
                    :aria-busy="isActivating || renewing ? 'true' : 'false'"
                    @click="showRenew ? openRenew() : open()"
                >
                    <v-progress-circular
                        v-if="isActivating || renewing"
                        indeterminate
                        size="16"
                        width="2"
                        color="#fff"
                    />
                    <span v-else>{{ bannerButton }}</span>
                </button>
            </div>
        </div>

        <v-dialog v-model="giftOpen" max-width="552" content-class="sms-gift-dialog">
            <div class="sms-gift" role="dialog" aria-labelledby="sms-gift-title">
                <header class="sms-gift__head">
                    <span class="sms-gift__mark" aria-hidden="true">
                        <v-icon size="22">mdi-gift-outline</v-icon>
                    </span>
                    <button type="button" class="sms-gift__close" aria-label="Закрыть" @click="giftOpen = false">
                        <v-icon size="18">mdi-close</v-icon>
                    </button>
                </header>
                <div class="sms-gift__body">
                    <h2 id="sms-gift-title">Дарим SMS-канал на {{ giftDaysLabel }}</h2>
                    <p class="sms-gift__lead">
                        Напоминайте о записи и отправляйте сообщения клиентам с рабочего номера.
                    </p>
                    <article class="sms-gift__benefit">
                        <v-icon size="20" color="#525AD5">mdi-layers-outline</v-icon>
                        <div>
                            <strong>Дополнительный канал бесплатно</strong>
                            <p>Сверх тарифа, без замены ваших мессенджеров.</p>
                        </div>
                    </article>
                    <article class="sms-gift__benefit">
                        <v-icon size="20" color="#525AD5">mdi-cellphone</v-icon>
                        <div>
                            <strong>Понадобится Android + SIM-карта</strong>
                            <p>Телефон должен быть включён и подключён к интернету.</p>
                        </div>
                    </article>
                    <h3>Как оплачиваются SMS</h3>
                    <ul>
                        <li>SMS — из пакета вашего оператора.</li>
                        <li>Data Heroes не берёт плату за отправки.</li>
                    </ul>
                    <p class="sms-gift__condition">
                        Подарок активируется после нажатия «Попробовать».
                        <strong>Без автопродления.</strong>
                    </p>
                    <p v-if="isDoctorPlan" class="sms-gift__note">
                        На «Доктор-боте» — для рассылок и уведомлений. Переписка Мультичата остаётся недоступна.
                    </p>
                </div>
                <footer class="sms-gift__foot">
                    <button type="button" class="sms-gift__later" :disabled="activating" @click="onGiftLater">
                        Позже
                    </button>
                    <button type="button" class="sms-gift__try" :disabled="activating" @click="activateGift">
                        {{ activating ? 'Проверяем…' : 'Попробовать' }}
                    </button>
                </footer>
            </div>
        </v-dialog>

        <v-dialog v-model="crmOpen" max-width="552" content-class="sms-crm-dialog">
            <MultichatSmsPromoPanel
                v-if="crmOpen"
                :bootstrap="crmBootstrap"
                main-view="sms_crm_offer"
                @later="crmOpen = false"
                @to-chat="crmOpen = false"
                @crm-submitted="onCrmSubmitted"
            />
        </v-dialog>
    </div>
</template>

<script>
import { activatePromo, getPromosByProject, PROMO_ACTIVATE_ERROR_TOAST } from '@/api/promo'
import { getSlots } from '@/api/networkFixer'
import { findPendingSmsPromo, splitByProjectPromos } from '@/helpers/promo/parsePromoBundle'
import {
    findActiveSmsPromo,
    isSmsDiscountActivation,
    smsRemainingLabel,
} from '@/helpers/gcb2/multichat/smsPromoDock'
import { resolveSmsOfferKind } from '@/helpers/promo/availableActivationTypes'
import { findFreeSmsPromoSlotId } from '@/helpers/channels/channelSlots'
import { openSmsPromoRenewalPayment } from '@/helpers/channels/openSmsPromoRenewal'
import { openChannelCreationSidebar } from '@/helpers/channels/openChannelCreationSidebar'
import { resolveMultichatPlan } from '@/helpers/gcb2/multichat/resolvePlan'
import { useBillingStore } from '@/store/stores/billing'
import { CHANNEL_TYPES, PROVIDER_TYPES } from '@/vars/general'
import MultichatSmsPromoPanel from '@/components/chisai/GCB2/chat/MultichatSmsPromoPanel.vue'
import { logSmsPromoButton } from '@/helpers/ymLogger'

const smsLogo = require('@/assets/img/messenger-logo/sms-logo.png')

function dayWord(days) {
    const n = Math.abs(days) % 100
    const n1 = n % 10
    if (n > 10 && n < 20) return 'дней'
    if (n1 === 1) return 'день'
    if (n1 >= 2 && n1 <= 4) return 'дня'
    return 'дней'
}

export default {
    name: 'ChannelsSmsPromoBanner',
    components: { MultichatSmsPromoPanel },
    props: {
        project: {
            type: Object,
            required: true,
        },
    },
    data: () => ({
        pendingPromos: [],
        activePromos: [],
        giftOpen: false,
        crmOpen: false,
        smsLogo,
        activating: false,
        renewing: false,
        connecting: false,
    }),
    computed: {
        pendingSms() {
            return findPendingSmsPromo(this.pendingPromos)
        },
        activeSms() {
            const promo = findActiveSmsPromo(this.activePromos)
            if (promo?.relevance === 'already_installed') return null
            return promo
        },
        discountReceived() {
            return Boolean(this.activeSms && isSmsDiscountActivation(this.activeSms.activationType))
        },
        smsExists() {
            const list = this.project?.connectors || []
            return list.some(
                (connector) =>
                    connector?.connectorType === CHANNEL_TYPES.smsChannel ||
                    connector?.providerType === PROVIDER_TYPES.smsGate
            )
        },
        isActivating() {
            return this.pendingSms?.status === 'activating'
        },
        offerKind() {
            if (!this.pendingSms || this.isActivating) return null
            return resolveSmsOfferKind(this.pendingSms.availableActivationTypes, this.smsExists)
        },
        showOffer() {
            return Boolean(this.pendingSms && (this.isActivating || this.offerKind))
        },
        showActivatedDiscount() {
            return this.discountReceived && !this.showOffer
        },
        showActiveGift() {
            return Boolean(this.activeSms && !this.discountReceived && !this.showOffer)
        },
        giftPeriodLabel() {
            return smsRemainingLabel(this.activeSms?.daysLeft ?? null)
        },
        showRenew() {
            return this.showActiveGift && this.activeSms?.subscriptionId != null
        },
        showConnect() {
            if (!this.showActiveGift || this.smsExists) return false
            const promo = this.activeSms
            if (!promo || promo.status === 'expired') return false
            if (promo.daysLeft != null && promo.daysLeft <= 0) return false
            return true
        },
        isDiscount() {
            return this.offerKind === 'discount'
        },
        showHost() {
            return Boolean(
                this.showOffer ||
                    this.showActivatedDiscount ||
                    this.showActiveGift ||
                    this.giftOpen ||
                    this.crmOpen
            )
        },
        giftDays() {
            const days = Number(this.pendingSms?.durationDays)
            return Number.isFinite(days) && days > 0 ? days : 30
        },
        giftDaysLabel() {
            return `${this.giftDays} ${dayWord(this.giftDays)}`
        },
        bannerTitle() {
            if (this.showActivatedDiscount) return 'Скидка 10% на внедрение CRM'
            if (this.showActiveGift) {
                if (this.activeSms?.status === 'expired' || (this.activeSms?.daysLeft != null && this.activeSms.daysLeft <= 0)) {
                    return 'SMS DH приостановлен'
                }
                if (this.activeSms?.status === 'need_prolong' || (this.activeSms?.daysLeft != null && this.activeSms.daysLeft <= 3)) {
                    return 'Подарок SMS DH скоро закончится'
                }
                return 'Подарок SMS DH активен'
            }
            if (this.isDiscount) return 'SMS подключён — вам скидка 10%'
            return `У вас подарок — SMS-канал на ${this.giftDaysLabel}`
        },
        bannerSub() {
            if (this.showActivatedDiscount) return 'Скидка получена.'
            if (this.showActiveGift) return this.giftPeriodLabel
            if (this.isDiscount) return 'На внедрение CRM-маркетинга. Приглашаем на диагностику.'
            return 'Нажмите для подключения.'
        },
        bannerButton() {
            if (this.showRenew) return 'Продлить'
            if (this.isActivating) return 'Подключаем…'
            return this.isDiscount ? 'Получить скидку' : 'Подключить бесплатно'
        },
        isDoctorPlan() {
            const projectId = this.project?.id
            if (!projectId) return false
            const current = useBillingStore().getCurrentTariff(projectId)
            if (!current?.integrationId) return false
            return resolveMultichatPlan(projectId) === 'doctor_bot'
        },
        crmBootstrap() {
            return {
                projectId: String(this.project?.id || ''),
                plan: this.isDoctorPlan ? 'doctor_bot' : 'smart_bot',
                permissions: { manageChannels: true, manageBilling: false },
                pendingPromos: this.pendingPromos,
                activePromos: this.activePromos,
                pendingPromo: this.pendingSms,
                slots: [],
                sms: {
                    exists: this.smsExists,
                    connected: this.smsExists,
                    ready: false,
                    connectorId: null,
                },
            }
        },
    },
    watch: {
        'project.id': {
            immediate: true,
            handler() {
                this.loadPromos()
            },
        },
    },
    created() {
        this.$root.$on('reload-project-promos', this.loadPromos)
    },
    beforeDestroy() {
        this.$root.$off('reload-project-promos', this.loadPromos)
    },
    methods: {
        async loadPromos() {
            const projectId = this.project?.id
            if (!projectId) {
                this.pendingPromos = []
                this.activePromos = []
                return
            }
            const { data, error } = await getPromosByProject(String(projectId))
            if (error) return
            const split = splitByProjectPromos(data)
            this.pendingPromos = split.pendingPromos
            this.activePromos = split.activePromos
        },
        async connectExistingGift() {
            if (this.connecting || this.smsExists) return
            this.connecting = true
            try {
                await this.openSmsConnectSidebar()
            } finally {
                this.connecting = false
            }
        },
        async openRenew() {
            const projectId = this.project?.id
            const promo = this.activeSms
            if (!projectId || !promo || this.renewing) return
            // Кнопка «Продлить»
            logSmsPromoButton('dh_sms_renew')
            this.renewing = true
            try {
                const result = await openSmsPromoRenewalPayment(String(projectId), promo)
                if (result === 'missing-slot') {
                    this.$store.dispatch('callNotify', 'Не найден слот промо для продления')
                }
            } finally {
                this.renewing = false
            }
        },
        open() {
            if (this.isActivating || !this.offerKind) return
            if (this.isDiscount) {
                this.crmOpen = true
            } else {
                // Кнопка «Подключить бесплатно» (открывает модалку подарка)
                logSmsPromoButton('dh_sms_promo_connect_free')
                this.giftOpen = true
            }
        },
        onGiftLater() {
            // Кнопка «Позже» в модалке подарка
            logSmsPromoButton('dh_sms_promo_later')
            this.giftOpen = false
        },
        async activateGift() {
            // Кнопка «Попробовать» в модалке подарка
            logSmsPromoButton('dh_sms_promo_try')
            await this.activate('integration')
        },
        async onCrmSubmitted() {
            await this.activate('discount')
        },
        async activate(activationType) {
            const key = this.pendingSms?.key
            const projectId = this.project?.id
            if (activationType === 'integration' && this.offerKind !== 'integration') return
            if (this.activating || !key || !projectId) return
            this.activating = true
            try {
                const { data, error } = await activatePromo({
                    key: String(key),
                    projectId: String(projectId),
                    activationType,
                })
                if (error || !data) {
                    this.$store.dispatch('callNotify', PROMO_ACTIVATE_ERROR_TOAST)
                    return
                }
                await this.loadPromos()
                this.$store.dispatch(
                    'callNotify',
                    data.alreadyActivated
                        ? 'Промо уже было активировано'
                        : activationType === 'discount'
                          ? 'Скидка на диагностику сохранена'
                          : 'Промо SMS DH активировано'
                )
                if (activationType === 'integration') {
                    this.giftOpen = false
                    await this.openSmsConnectSidebar()
                }
            } finally {
                this.activating = false
            }
        },
        async openSmsConnectSidebar() {
            const projectId = this.project?.id
            if (projectId) {
                try {
                    await this.$store.dispatch('project/setFastProjectById', String(projectId))
                } catch {
                    // слот мог появиться и без обновления проекта
                }
                try {
                    this.$root.$emit('reload-channel-slots')
                } catch {
                    // таблица каналов может быть не смонтирована
                }
            }
            const slotId = await this.waitForFreeSmsSlot()
            openChannelCreationSidebar(
                this,
                {
                    group: 'sms',
                    channelType: PROVIDER_TYPES.smsGate,
                    _slotId: slotId,
                },
                'new',
                { context: 'sms-promo' }
            )
        },
        async waitForFreeSmsSlot() {
            const projectId = this.project?.id
            if (!projectId) return null
            for (let attempt = 0; attempt < 3; attempt += 1) {
                const { data, error } = await getSlots({ projectId: String(projectId) })
                if (!error) {
                    const slotId = findFreeSmsPromoSlotId(Array.isArray(data) ? data : [])
                    if (slotId) return slotId
                }
                if (attempt < 2) await new Promise((resolve) => setTimeout(resolve, 400))
            }
            return null
        },
    },
}
</script>

<style lang="sass" scoped>
@import '@/vars.sass'

.sms-project-promo
    margin-top: 14px
    display: flex
    align-items: center
    gap: 14px
    padding: 14px 18px
    border-radius: 16px
    background: #fff4f8
    border: 1px solid #f3bed1
    font-family: $font-family-base

.sms-project-promo__icon
    width: 36px
    height: 36px
    border-radius: 11px
    background: transparent
    display: flex
    align-items: center
    justify-content: center
    flex-shrink: 0
    overflow: hidden
    padding: 0
    img
        width: 100%
        height: 100%
        object-fit: cover
        display: block

.sms-project-promo__copy
    flex: 1
    min-width: 0

.sms-project-promo__title
    font-size: 13.5px
    line-height: 1.2
    font-weight: 700
    color: #8e1d4e

.sms-project-promo__sub
    margin-top: 2px
    font-size: 12px
    line-height: 1.45
    color: #755060

.sms-project-promo__actions
    margin-left: auto
    display: flex
    align-items: center
    gap: 8px
    flex-shrink: 0

.sms-project-promo__btn
    flex-shrink: 0
    border: 0
    border-radius: 10px
    padding: 8px 16px
    background: #525ad5
    color: #fff
    font-family: $font-family-base
    span
        color: #fff
    display: inline-flex
    align-items: center
    justify-content: center
    min-width: 108px
    min-height: 36px
    font-size: 13px
    font-weight: 600
    line-height: 1.2
    cursor: pointer
    white-space: nowrap
    &:hover
        background: #454cc0
    &:disabled
        opacity: .7
        cursor: default
    &:focus-visible
        outline: 2px solid rgba(82, 90, 213, .45)
        outline-offset: 2px
    &--quiet
        background: #fff
        color: #525ad5
        border: 1px solid #d8d4ea
        span
            color: #525ad5
        &:hover
            background: #f7f8ff

.sms-gift
    background: #fff
    border-radius: 24px
    overflow: hidden
    color: #2b3048
    font-family: $font-family-base
    h2
        margin: 16px 0 0
        font-size: 28px
        line-height: 1.15
        font-weight: 700
        color: #1a1a2e
    h3
        margin: 18px 0 8px
        font-size: 16px
        line-height: 1.3
        font-weight: 700
        color: #1a1a2e
    p, li, span
        color: #596278
    strong
        color: #1a1a2e
    ul
        margin: 0
        padding-left: 18px
    li + li
        margin-top: 4px

.sms-gift__head
    display: flex
    align-items: flex-start
    justify-content: space-between
    padding: 20px 20px 0

.sms-gift__mark
    width: 44px
    height: 44px
    border-radius: 14px
    background: #d4ec38
    display: flex
    align-items: center
    justify-content: center
    ::v-deep .v-icon
        color: #1a1a2e !important

.sms-gift__close
    width: 36px
    height: 36px
    border-radius: 12px
    border: 1px solid #e5e8f0
    background: #fff
    display: flex
    align-items: center
    justify-content: center
    cursor: pointer
    ::v-deep .v-icon
        color: #596278 !important

.sms-gift__body
    padding: 4px 24px 8px

.sms-gift__lead
    margin: 8px 0 16px
    font-size: 15px
    line-height: 1.45

.sms-gift__benefit
    display: flex
    gap: 12px
    padding: 14px 0
    border-top: 1px solid #eef0f4
    strong
        display: block
        font-size: 15px
        line-height: 1.3
    p
        margin: 2px 0 0
        font-size: 14px
        line-height: 1.4
    ::v-deep .v-icon
        color: #525ad5 !important

.sms-gift__condition
    margin: 14px 0 0
    font-size: 14px
    line-height: 1.45
    strong
        font-weight: 700

.sms-gift__note
    margin: 8px 0 0
    font-size: 14px
    line-height: 1.45

.sms-gift__foot
    display: flex
    align-items: center
    justify-content: space-between
    gap: 12px
    padding: 16px 20px
    border-top: 1px solid #eef0f4

.sms-gift__later,
.sms-gift__try
    border: 0
    cursor: pointer
    font-family: $font-family-base
    font-size: 15px
    font-weight: 600
    &:disabled
        opacity: .7
        cursor: default

.sms-gift__later
    background: transparent
    color: #596278
    padding: 10px 8px

.sms-gift__try
    background: #525ad5
    color: #fff
    border-radius: 14px
    padding: 12px 22px
    &:hover:not(:disabled)
        background: #454cc0

@media (max-width: 820px)
    .sms-project-promo
        flex-wrap: wrap
        align-items: center
        row-gap: 12px
    .sms-project-promo__actions
        flex: 1 0 100%
        margin-left: 0
        flex-direction: column
        align-items: stretch
    .sms-project-promo__btn
        width: 100%
        min-width: 0
</style>

<style lang="sass">
.sms-gift-dialog
    border-radius: 24px
    overflow: hidden
    box-shadow: 0 24px 80px rgba(17, 22, 41, .25)

.sms-crm-dialog
    background: transparent
    box-shadow: none
    overflow: visible
</style>
